// Pure DSP helpers for MP3 encoding, shared by the one-shot encoder worker
// (mp3Encoder.worker.js, via Task 6) and the streaming encoder
// (mp3StreamSession.js + mp3StreamEncoder.worker.js). Kept free of `self` and
// lamejs so they run in jsdom and are unit-testable directly.

export const SAMPLES_PER_FRAME = 1152;
export const TARGET_RATE = 16000;

// Pick CBR bitrate from recording duration (matches the mobile app's strategy).
export function pickBitrate(durationSecs) {
  const minutes = durationSecs / 60;
  if (minutes <= 30) return 48;
  if (minutes <= 60) return 40;
  if (minutes <= 120) return 32;
  return 24;
}

// Nearest-neighbour downsample. Good enough for speech; the encoder's lowpass
// filters high-freq artefacts.
export function downsample(samples, fromRate, toRate) {
  if (fromRate === toRate) return samples;
  const ratio = fromRate / toRate;
  const newLength = Math.floor(samples.length / ratio);
  const result = new Float32Array(newLength);
  for (let i = 0; i < newLength; i++) {
    result[i] = samples[Math.floor(i * ratio)];
  }
  return result;
}

// Float32 PCM (-1..1) -> Int16.
export function floatToInt16(samples) {
  const int16 = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    int16[i] = s < 0 ? s * 32768 : s * 32767;
  }
  return int16;
}

// Average N channels into one mono Float32Array of `length` samples.
export function mixToMono(channels, length) {
  const mono = new Float32Array(length);
  const n = channels.length;
  for (let c = 0; c < n; c++) {
    const ch = channels[c];
    for (let i = 0; i < length; i++) mono[i] += ch[i];
  }
  if (n > 1) {
    for (let i = 0; i < length; i++) mono[i] /= n;
  }
  return mono;
}

// Uint8Array -> base64 (chunked to avoid a btoa stack overflow on big inputs).
export function uint8ArrayToBase64(bytes) {
  let binary = "";
  const CHUNK = 32768;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}
